import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CookiePreferencesLink } from '@resida/analytics';
import { PolicyModal } from './PolicyModal';
import { FOOTER_LINKS } from '../../data/navigation';
import privacyPolicyMd from '../../../../../docs/policies/privacy-policy.md?raw';
import termsOfUseMd from '../../../../../docs/policies/terms-of-use.md?raw';

type PolicyType = 'privacy' | 'terms' | null;

// 44px tap targets on mobile; denser on desktop.
const LEGAL_LINK_CLASS =
  'inline-flex items-center min-h-[44px] md:min-h-0 text-sm text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] transition-colors';

const POLICY_CONFIG = {
  privacy: { title: 'Privacy Policy', content: privacyPolicyMd },
  terms: { title: 'Terms of Service', content: termsOfUseMd },
};

export function Footer(): JSX.Element {
  const [openPolicy, setOpenPolicy] = useState<PolicyType>(null);

  return (
    <>
      <footer
        data-testid="landing-footer"
        className="bg-[var(--landing-surface)] border-t border-[var(--landing-border)]"
      >
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Top: Logo + Link columns */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo + tagline */}
            <div>
              <Link to="/" className="text-xl font-bold text-[var(--landing-accent-text)]">
                Resida
              </Link>
              <p className="text-sm text-[var(--landing-text-muted)] mt-2">
                Rental management for independent landlords with 1 to 20 units.
              </p>
            </div>

            {/* Feature columns */}
            {FOOTER_LINKS.features.map((group) => (
              <div key={group.group}>
                <h4 className="text-xs font-medium uppercase tracking-widest text-[var(--landing-text-muted)] mb-3">
                  {group.group}
                </h4>
                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className="text-sm text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] transition-colors"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Company column */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-widest text-[var(--landing-text-muted)] mb-3">
                Company
              </h4>
              <ul className="space-y-2">
                {FOOTER_LINKS.company.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="text-sm text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href="mailto:support@resida.me"
                    className="text-sm text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] transition-colors"
                  >
                    support@resida.me
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom: legal + copyright */}
          <div className="flex flex-col items-center gap-3 md:flex-row md:justify-between md:items-center border-t border-[var(--landing-border)] pt-6">
            <div className="flex gap-4">
              <button
                type="button"
                data-testid="footer-privacy-link"
                onClick={() => setOpenPolicy('privacy')}
                className={LEGAL_LINK_CLASS}
              >
                Privacy Policy
              </button>
              <button
                type="button"
                data-testid="footer-terms-link"
                onClick={() => setOpenPolicy('terms')}
                className={LEGAL_LINK_CLASS}
              >
                Terms of Service
              </button>
              <CookiePreferencesLink className={LEGAL_LINK_CLASS} />
            </div>
            <p className="text-sm text-[var(--landing-text-muted)]">
              &copy; 2026 Resida. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {openPolicy && (
        <PolicyModal
          title={POLICY_CONFIG[openPolicy].title}
          content={POLICY_CONFIG[openPolicy].content}
          onClose={() => setOpenPolicy(null)}
        />
      )}
    </>
  );
}
